import type { KeyboardEvent, ReactNode } from "react";

import { cn } from "@krea/shared/utils";

export interface IInformationBoxProps {
  children: ReactNode;
  link?: string;
  className?: string;
  /** Overrides the inner flex wrapper (default: centered column). */
  innerClassName?: string;
  /**
   * `heroPill` — gray pill, content-sized, left-aligned row (hero review carousel).
   * Default — full-width centered block (login / cards).
   */
  variant?: "default" | "heroPill";
}

export const InformationBox = ({
  children,
  link,
  className,
  innerClassName,
  variant = "default",
}: IInformationBoxProps) => {
  const openLink = () => link && window.open(link, "_blank", "noopener");

  return (
    <div
      className={cn(
        variant === "heroPill"
          ? "flex h-8 w-fit max-w-full items-center rounded-full bg-gray-100 px-2.5 text-left md:h-10 md:px-4"
          : "min-h-[50px] w-full px-2 py-1 text-center content-center",
        link && link !== "" ? "cursor-pointer" : "cursor-default",
        className,
      )}
      data-href={link}
      onClick={openLink}
      role="button"
      onKeyDown={(e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openLink();
        }
      }}
      tabIndex={link ? 0 : -1}
    >
      <div
        className={cn(
          variant === "heroPill"
            ? "flex h-full min-h-0 flex-row items-center justify-start opacity-100"
            : "flex h-full min-h-0 flex-col items-center justify-center opacity-75",
          innerClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
};
